import {
  useEffect,
  useState,
  useRef,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { toSlug } from "../contants";
import { getCategoryTree } from "../server/category/getTree";

type CategoryNode = {
  id: string;
  name: string;
  children?: CategoryNode[];
};

export function CategorySidebar() {
  const [data, setData] = useState<CategoryNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState<{
    id: string;
    left: number;
    top: number;
  } | null>(null);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const res = await getCategoryTree();
        setData(res || []);
      } catch (err) {
        console.error("Lỗi lấy category:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    const handleHide = () => setHovered(null);
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-category-popover='true']")) {
        return;
      }
      handleHide();
    };

    window.addEventListener("resize", handleHide);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", handleHide);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const clearHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const scheduleHide = () => {
    clearHide();
    hideTimer.current = window.setTimeout(() => setHovered(null), 120);
  };

  const handleMouseEnter = (
    item: CategoryNode,
    e: MouseEvent<HTMLLIElement>
  ) => {
    const hasChildren = (item?.children?.length ?? 0) > 0;
    if (!hasChildren) {
      setHovered(null);
      return;
    }
    clearHide();
    const rect = e.currentTarget.getBoundingClientRect();
    setHovered({
      id: item.id,
      left: rect.right + 8,
      top: rect.top,
    });
  };

  const renderNestedChildren = (
    nodes: CategoryNode[],
    level: number = 0
  ): ReactNode[] => {
    return (nodes ?? []).map((child) => {
      const hasChild = (child.children?.length ?? 0) > 0;
      return (
        <div
          key={child.id}
          className="space-y-1"
          style={{ marginLeft: level ? 12 : 0 }}
        >
          <a
            href={`/danh-muc-san-pham/${toSlug(child?.name ?? "")}/${child?.id}`}
            className="block rounded-md px-2 py-1 text-sm font-medium text-slate-800 transition hover:bg-indigo-50 hover:text-indigo-700"
          >
            {child.name}
          </a>
          {hasChild ? (
            <div className="border-l border-slate-100 pl-3">
              {renderNestedChildren(child.children ?? [], level + 1)}
            </div>
          ) : null}
        </div>
      );
    });
  };

  return (
    <aside className="glass-card h-full w-full border border-white/10 bg-white text-slate-900 shadow-lift relative z-10">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <p className="section-eyebrow">Danh mục</p>
        </div>
        <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
          {data.length} nhóm
        </span>
      </div>

      <div className="max-h-[360px] overflow-y-hidden hover:overflow-y-auto px-4 py-3 scroll-rounded transition-[overflow]">
        {loading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {data?.slice(0, 12)?.map((item) => {
              const hasChildren = (item?.children?.length ?? 0) > 0;
              return (
                <li
                  key={item?.id}
                  className="group relative"
                  onMouseEnter={(e) => handleMouseEnter(item, e)}
                  onMouseLeave={scheduleHide}
                >
                  <a
                    href={`/danh-muc-san-pham/${toSlug(item?.name ?? "")}/${item?.id}`}
                    className="flex items-center justify-between rounded-lg px-3 py-2 transition hover:-translate-x-1 hover:bg-slate-50"
                  >
                    <span className="font-semibold text-slate-900 group-hover:text-indigo-700">
                      {item?.name}
                    </span>
                    {hasChildren ? (
                      <span className="text-xs font-semibold text-indigo-600">
                        {(item.children?.length ?? 0)}+
                      </span>
                    ) : null}
                  </a>

                  {hasChildren &&
                    hovered?.id === item.id &&
                    typeof document !== "undefined" &&
                    createPortal(
                      <div
                        className="pointer-events-auto fixed z-50 min-w-[260px] rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur"
                        style={{ left: hovered.left, top: hovered.top }}
                        onMouseEnter={clearHide}
                        onMouseLeave={scheduleHide}
                        data-category-popover="true"
                      >
                        <div className="mb-2 text-xs font-semibold uppercase text-slate-400">
                         Danh mục {item.name}
                        </div>
                        <div className="max-h-[320px] space-y-2 overflow-y-auto pr-2">
                          {renderNestedChildren(item.children ?? [])}
                        </div>
                      </div>,
                      document.body
                    )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
